import { npcRefusalOf } from '@/lib/npc-labels';
import { eurosLabel, modelName } from '@/lib/npc-replays-labels';
import type {
  ModelCatalogEntry,
  ModelSeat,
  NpcEstimate,
  NpcLaunch,
  NpcLaunchPlayer,
  ReplayMode,
  VersionTag,
  VersionedDetail,
} from '@matter_extractor/shared';
import {
  NPC_PASSES_MAX,
  NPC_PASSES_MIN,
  REPLAY_DEFAULT_PLAYER_SEAT,
} from '@matter_extractor/shared';

/**
 * What the launch box says and sends (npc.md §6, issue #210), PURE.
 *
 * The box is a form over `npcLaunchSchema`: this file holds the draft of its fields, builds the body
 * from it, and words what the server answers - the estimate of §6.2 and the refusals of §17 a launch
 * can meet. Nothing here computes a cost: the estimate is the server's, and the box prints it.
 */

/* ------------------------------------------------------------------- the draft */

/** What the box asks for, as the operator filled it. The target is not in it: it is what was opened. */
export interface LaunchDraft {
  readonly experienceId: string;
  readonly tag: VersionTag;
  readonly mode: ReplayMode;
  readonly passes: number;
  readonly player: 'server' | 'claude';
  /** The model of the player for THIS launch (N7), or null: the model of Réglages > Rejeu. */
  readonly playerModel: string | null;
  /** A version of Réglages > Modèles from the history (N8), or null: the ones in force. */
  readonly modelsVersionId: string | null;
}

/** The defaults of §6.1: development, strict, one pass, played by the server, the models in force. */
export const OPENING_LAUNCH_DRAFT: LaunchDraft = {
  experienceId: '',
  tag: 'development',
  mode: 'strict',
  passes: NPC_PASSES_MIN,
  player: 'server',
  playerModel: null,
  modelsVersionId: null,
};

/** What a launch targets, and what the box says of it. */
export type LaunchTarget =
  | { readonly kind: 'npc'; readonly npcId: string; readonly name: string }
  | {
      readonly kind: 'bench';
      readonly benchId: string;
      readonly name: string;
      readonly npcNames: readonly string[];
    };

/** The passes as the number field holds them: a whole number clamped to 1 to 5, or null while blank. */
export function passesOf(text: string): number | null {
  const value = Number.parseInt(text, 10);
  if (!Number.isInteger(value)) return null;
  return Math.min(NPC_PASSES_MAX, Math.max(NPC_PASSES_MIN, value));
}

/**
 * The seat the server's player plays on for this launch: the model picked in the box with the
 * tariff the catalog gave it, or null when the player is Claude or the model of Réglages > Rejeu
 * stands (the body then says nothing, and the server uses its own).
 */
export function playerSeatOf(
  draft: Pick<LaunchDraft, 'player' | 'playerModel'>,
  base: ModelSeat,
  catalog: readonly ModelCatalogEntry[],
): ModelSeat | null {
  if (draft.player !== 'server' || draft.playerModel === null) return null;
  if (draft.playerModel === base.model) return null;
  const entry = catalog.find(
    (candidate) => candidate.route === base.route && candidate.model === draft.playerModel,
  );
  if (entry === undefined || entry.pricing === null) return null;
  return { route: entry.route, model: entry.model, provider: null, pricing: entry.pricing };
}

/** The body of the estimate and of the launch, or null while no experience is chosen. */
export function launchInput(
  target: LaunchTarget,
  draft: LaunchDraft,
  seat: ModelSeat | null,
): NpcLaunch | null {
  if (draft.experienceId === '') return null;
  const player: NpcLaunchPlayer =
    draft.player === 'claude'
      ? { kind: 'claude' }
      : seat === null
        ? { kind: 'server' }
        : { kind: 'server', model: seat };
  return {
    target: target.kind === 'npc' ? { npcId: target.npcId } : { benchId: target.benchId },
    experienceId: draft.experienceId,
    tag: draft.tag,
    mode: draft.mode,
    passes: draft.passes,
    player,
    ...(draft.modelsVersionId === null ? {} : { modelsVersionId: draft.modelsVersionId }),
  };
}

/* ------------------------------------------------------------------ the words */

/** « 1 rejeu », « 16 rejeux ». */
export function replaysWord(count: number): string {
  return `${count} ${count > 1 ? 'rejeux' : 'rejeu'}`;
}

/** The sentence under the passes: « Chaque NPC du banc est joué deux fois : 8 NPC × 2 passes = 16 rejeux. » */
export function passesSentence(target: LaunchTarget, passes: number): string {
  if (target.kind === 'npc') {
    return passes > 1 ? `Ce NPC est joué ${passes} fois : ${replaysWord(passes)}.` : '';
  }
  const count = target.npcNames.length;
  const total = count * passes;
  return `Chaque NPC du banc est joué ${passes > 1 ? `${passes} fois` : 'une fois'} : ${count} NPC × ${passes} ${passes > 1 ? 'passes' : 'passe'} = ${replaysWord(total)}.`;
}

/** The launch button: « Lancer le rejeu » for one, « Lancer 16 rejeux » for several. */
export function launchButtonLabel(replays: number | null): string {
  return replays === null || replays <= 1 ? 'Lancer le rejeu' : `Lancer ${replays} rejeux`;
}

/** « 21 € » above two euros, « 1,30 € » below: an estimate is not told to the cent when it is large. */
export function approximateEuros(value: number): string {
  return value >= 2 ? `${Math.round(value)} €` : eurosLabel(value);
}

/** What the box says of the line « 16 rejeux, environ 21 € côté dispositif et 4 € côté joueur ». */
export function estimateHeadline(estimate: NpcEstimate, player: 'server' | 'claude'): string {
  const count = replaysWord(estimate.replays);
  if (estimate.impossible || estimate.dispositifEur === null) {
    return `${count}, estimation impossible`;
  }
  const device = `environ ${approximateEuros(estimate.dispositifEur)} côté dispositif`;
  if (player === 'claude') return `${count}, ${device}, joueur gratuit`;
  return estimate.playerEur === null
    ? `${count}, ${device}`
    : `${count}, ${device} et ${approximateEuros(estimate.playerEur)} côté joueur`;
}

/** The note under the headline, when part of the estimate has nothing to stand on. */
export function estimateGapSentence(estimate: NpcEstimate): string | null {
  if (estimate.impossible || estimate.unestimatedReplays === 0) return null;
  return `${replaysWord(estimate.unestimatedReplays)} n’${estimate.unestimatedReplays > 1 ? 'ont' : 'a'} rien sur quoi s’appuyer : ${estimate.unestimatedReplays > 1 ? 'ils sont' : 'il est'} hors du total.`;
}

/**
 * How the estimate was made (H14), and that it is NOT a ceiling (N19). The sentence follows the
 * method the server reports: the last finished replay of each NPC, the median of the experience, or
 * nothing to lean on.
 */
export function estimateMethodSentence(
  estimate: NpcEstimate,
  experienceName: string,
  playerSeatLabel: string | null,
): string {
  if (estimate.impossible) {
    return 'Aucun rejeu n’a encore fini sur cette expérience : rien sur quoi s’appuyer. Le coût se lira sur chaque ligne de la série, à mesure. Pas de plafond : la série va au bout.';
  }
  const how: Record<NpcEstimate['method'], string> = {
    'last-run': `pour chaque NPC, le coût du dernier rejeu fini de ce NPC sur ${experienceName}, multiplié par ses passes`,
    median: `aucun de ces NPC n’a fini de rejeu sur ${experienceName} : pour chacun, le coût médian des rejeux finis de l’expérience`,
    mixed: `pour chaque NPC, le coût de son dernier rejeu fini sur ${experienceName}, ou à défaut le coût médian des rejeux finis de l’expérience`,
    impossible: '',
  };
  const player = playerSeatLabel === null ? '' : `, le joueur au tarif de ${playerSeatLabel}`;
  return `Calcul : ${how[estimate.method]}${player}. Une estimation, pas un plafond : la série va au bout.`;
}

/** « v24 » beside the side chosen, or nothing when the experience has no version there. */
export function sideVersionLabel(
  side: VersionTag,
  pointers: { development: number | null; production: number | null },
): string | null {
  const number = side === 'production' ? pointers.production : pointers.development;
  return number === null ? null : `v${number}`;
}

/** The label of the model of the player in the select: « Claude Sonnet 5.5 (Réglages > Rejeu) ». */
export function playerModelOptionLabel(model: string, isDefault: boolean): string {
  return isDefault ? `${modelName(model)} (Réglages > Rejeu)` : modelName(model);
}

/** The models offered for the player of this launch: the default first, then the catalog's, once each. */
export function playerModelChoices(
  base: ModelSeat | null | undefined,
  catalog: readonly ModelCatalogEntry[],
): readonly { readonly model: string; readonly label: string }[] {
  const seat = base ?? REPLAY_DEFAULT_PLAYER_SEAT;
  const seen = new Set<string>([seat.model]);
  const choices = [{ model: seat.model, label: playerModelOptionLabel(seat.model, true) }];
  for (const entry of catalog) {
    if (entry.route !== seat.route || entry.pricing === null || seen.has(entry.model)) continue;
    seen.add(entry.model);
    choices.push({ model: entry.model, label: playerModelOptionLabel(entry.model, false) });
  }
  return choices;
}

/** One option of the history of Réglages > Modèles: « v6 · 22 oct. 2026 », with its note when it has one. */
export function modelsVersionOptions(
  models: Pick<VersionedDetail<unknown, unknown>, 'development' | 'production' | 'versions'>,
  tag: VersionTag,
  dayLabel: (iso: string) => string,
): readonly { readonly id: string; readonly label: string }[] {
  const inForce = tag === 'production' ? models.production : models.development;
  return [...models.versions]
    .sort((a, b) => b.number - a.number)
    .filter((version) => inForce === null || version.id !== inForce.id)
    .map((version) => ({
      id: version.id,
      label: [`v${version.number}`, version.note, dayLabel(version.createdAt)]
        .filter((part): part is string => part !== null && part !== '')
        .join(' · '),
    }));
}

/** The first option of the models select: « Ceux en vigueur côté développement · v7 ». */
export function modelsInForceLabel(
  models: Pick<VersionedDetail<unknown, unknown>, 'development' | 'production'>,
  tag: VersionTag,
): string {
  const inForce = tag === 'production' ? models.production : models.development;
  const side = tag === 'production' ? 'production' : 'développement';
  return inForce === null
    ? `Ceux en vigueur côté ${side}`
    : `Ceux en vigueur côté ${side} · v${inForce.number}`;
}

/* ----------------------------------------------------------------- refusals */

/** What a refused launch says: a short title, and the sentence that tells what to do. */
export interface LaunchRefusal {
  readonly title: string;
  readonly sentence: string;
}

/**
 * The refusal a launch or its estimate answered (§17), worded from its CODE, or null when it was
 * none. `no-protocol-page` is the one the box meets most (§6.1): an experience with no conversation.
 */
export function launchRefusalOf(error: unknown): LaunchRefusal | null {
  const refusal = npcRefusalOf(error);
  if (refusal === null) return null;
  switch (refusal.code) {
    case 'no-protocol-page':
      return {
        title: 'Rien à rejouer',
        sentence:
          'Cette expérience n’a pas de page protocole : un NPC n’y a personne à qui répondre. Choisissez une expérience avec une conversation.',
      };
    case 'entry-unresolved':
      return {
        title: 'Cette expérience ne se joue pas de ce côté',
        sentence:
          'Elle n’a pas de version sur ce côté : choisissez l’autre côté, ou une autre expérience.',
      };
    case 'bench-empty':
      return {
        title: 'Rien à rejouer',
        sentence:
          'Tous les NPC de ce banc sont retirés. Ajoutez-en un au banc, ou restaurez-en un.',
      };
    case 'npc-retired':
      return {
        title: 'Ce NPC est retiré',
        sentence: 'Restaurez-le avant de le lancer.',
      };
    case 'npc-unknown':
      return { title: 'Ce NPC n’existe plus', sentence: 'Rechargez la page.' };
    case 'bench-unknown':
      return { title: 'Ce banc n’existe plus', sentence: 'Rechargez la page.' };
    case 'passes-out-of-range':
      return { title: 'Passes hors bornes', sentence: 'Les passes vont de 1 à 5.' };
    case 'models-version-unknown':
      return {
        title: 'Version des Modèles inconnue',
        sentence:
          'Cette version de Réglages > Modèles n’existe plus. Choisissez-en une autre, ou les Modèles en vigueur.',
      };
    default:
      return null;
  }
}

/** The sentence a series gesture's refusal (stop, relaunch) says, or null for a code with none. */
export function seriesRefusalSentence(error: unknown): string | null {
  const refusal = npcRefusalOf(error);
  if (refusal === null) return null;
  switch (refusal.code) {
    case 'series-unknown':
      return 'Cette série n’existe plus.';
    case 'series-stopped':
      return 'Cette série est arrêtée : rien à relancer.';
    case 'no-protocol-page':
    case 'entry-unresolved':
      return launchRefusalOf(error)?.sentence ?? null;
    default:
      return null;
  }
}
